import { DashboardMapPinColour, PillVariant } from "../../widget";
import { ProjectStatusConstants } from "../status";

export enum ProjectPhase {
  feasibility = "feasibility",
  design = "design",
  implementation = "implementation",
  ongoingMonitoring = "ongoingMonitoring",
}

const normaliseProjectPhaseValue = (value: string): string => value.toLowerCase().replace(/[^a-z]/g, "");

const PROJECT_PHASE_LABEL_MAP: Record<ProjectPhase, string> = {
  [ProjectPhase.feasibility]: "Feasibility",
  [ProjectPhase.design]: "Design",
  [ProjectPhase.implementation]: "Implementation",
  [ProjectPhase.ongoingMonitoring]: "Ongoing monitoring",
};

export const parseProjectPhase = (value: string | null | undefined): ProjectPhase | undefined => {
  if (!value) return undefined;

  const normalisedValue = normaliseProjectPhaseValue(value);
  return Object.values(ProjectPhase).find((phase) => normaliseProjectPhaseValue(phase) === normalisedValue);
};

export const getProjectPhaseLabel = (value: string | null | undefined): string => {
  const parsedProjectPhase = parseProjectPhase(value);

  if (parsedProjectPhase) {
    return PROJECT_PHASE_LABEL_MAP[parsedProjectPhase];
  }

  return value ?? "";
};

/**
 * Folds `cachedPhase` aggregate rows into per-phase counts keyed by the display label, in phase order.
 * The API has moved `cachedPhase` from Title Case to sentence case ("Ongoing Monitoring" -> "Ongoing monitoring")
 * and older rows can still carry the old casing, so rows are matched by phase rather than by their raw key and
 * rows for the same phase are summed. Keys that are not a project phase (e.g. "Completed") are dropped.
 */
export const getProjectPhaseCounts = (rows: { key: string | null; value: number }[]): Record<string, number> => {
  const counts = Object.fromEntries(Object.values(PROJECT_PHASE_LABEL_MAP).map((label) => [label, 0]));

  rows.forEach(({ key, value }) => {
    const phase = parseProjectPhase(key);
    if (phase) counts[PROJECT_PHASE_LABEL_MAP[phase]] += value;
  });

  return counts;
};

export const PROJECT_PHASE_PILL_VARIANT_MAP: { [key: string]: PillVariant } = {
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.feasibility]]: "blue",
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.design]]: "coral",
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.implementation]]: "cyan",
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.ongoingMonitoring]]: "green",
};

export const PROJECT_PHASE_MAP_COLOUR_MAP: { [key: string]: DashboardMapPinColour } = {
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.feasibility]]: "goldenrod",
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.design]]: "magenta",
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.implementation]]: "sky-blue",
  [PROJECT_PHASE_LABEL_MAP[ProjectPhase.ongoingMonitoring]]: "green",
};

export const ValidProjectStatusProjectPhases = {
  [ProjectPhase.feasibility]: [
    ProjectStatusConstants.DRAFT,
    ProjectStatusConstants.PRE_DEVELOPMENT,
    ProjectStatusConstants.UNDER_DEVELOPMENT,
    ProjectStatusConstants.VALIDATION_PENDING,
    ProjectStatusConstants.VALIDATED,
    ProjectStatusConstants.VERIFICATION_PENDING,
    ProjectStatusConstants.VERIFIED,
    ProjectStatusConstants.ONGOING_VERIFICATION,
    ProjectStatusConstants.SETUP_IN_PROGRESS,
    ProjectStatusConstants.NOT_DELIVERED,
    ProjectStatusConstants.CANCELLED,
  ],
  [ProjectPhase.design]: [
    ProjectStatusConstants.UNDER_DEVELOPMENT,
    ProjectStatusConstants.VALIDATION_PENDING,
    ProjectStatusConstants.VALIDATED,
    ProjectStatusConstants.VERIFICATION_PENDING,
    ProjectStatusConstants.VERIFIED,
    ProjectStatusConstants.ONGOING_VERIFICATION,
    ProjectStatusConstants.SETUP_IN_PROGRESS,
    ProjectStatusConstants.NOT_DELIVERED,
    ProjectStatusConstants.CANCELLED,
  ],
  [ProjectPhase.implementation]: [
    ProjectStatusConstants.VALIDATION_PENDING,
    ProjectStatusConstants.VALIDATED,
    ProjectStatusConstants.VERIFICATION_PENDING,
    ProjectStatusConstants.VERIFIED,
    ProjectStatusConstants.ONGOING_VERIFICATION,
    ProjectStatusConstants.SETUP_IN_PROGRESS,
    ProjectStatusConstants.NOT_DELIVERED,
    ProjectStatusConstants.CANCELLED,
  ],
  [ProjectPhase.ongoingMonitoring]: [
    ProjectStatusConstants.VERIFIED,
    ProjectStatusConstants.ONGOING_VERIFICATION,
    ProjectStatusConstants.SETUP_IN_PROGRESS,
    ProjectStatusConstants.NOT_DELIVERED,
    ProjectStatusConstants.CANCELLED,
  ],
};
